import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Toaster, toast } from 'sonner';
import BookingWidget from '../components/BookingWidget';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error(t('toasts.fillRequired'));
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language,
      });
      setSubmitted(true);
      toast.success(t('contact.success'));
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('toasts.contactError'));
    }
    
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <div data-testid="contact-success" className="min-h-screen pt-32 pb-20 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-[#1E3A5F]" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl mb-4 text-[#1E3A5F]">
            {t('contact.success')}
          </h1>
          <p className="text-muted-foreground text-lg mb-8">
            {t('contact.thankYou')}
          </p>
          <Button
            data-testid="contact-back-home-btn"
            onClick={() => window.location.href = '/'}
            className="bg-[#1E3A5F] hover:bg-[#1E3A5F]/90 text-white"
          >
            {t('common.backToHome')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="min-h-screen pt-24 pb-32 bg-white">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <p className="uppercase tracking-widest text-xs text-[#D4A72C] mb-4">
          {t('nav.contact')}
        </p>
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1E3A5F] mb-6">
          {t('contact.title')}
        </h1>
        <p className="text-muted-foreground text-lg max-w-2xl">
          {t('contact.subtitle')}
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-[#f8fafc] border border-[#1E3A5F]/10 rounded-sm p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm text-[#1E3A5F]/70 mb-2">
                    {t('contact.name')} *
                  </label>
                  <Input
                    data-testid="contact-name-input"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="border-[#1E3A5F]/20 focus:border-[#D4A72C]"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#1E3A5F]/70 mb-2">
                    {t('contact.email')} *
                  </label>
                  <Input
                    data-testid="contact-email-input"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="border-[#1E3A5F]/20 focus:border-[#D4A72C]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm text-[#1E3A5F]/70 mb-2">
                    {t('contact.phone')}
                  </label>
                  <Input
                    data-testid="contact-phone-input"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="border-[#1E3A5F]/20 focus:border-[#D4A72C]"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#1E3A5F]/70 mb-2">
                    {t('contact.subject')} *
                  </label>
                  <Input
                    data-testid="contact-subject-input"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="border-[#1E3A5F]/20 focus:border-[#D4A72C]"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm text-[#1E3A5F]/70 mb-2">
                  {t('contact.message')} *
                </label>
                <Textarea
                  data-testid="contact-message-input"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  required
                  className="border-[#1E3A5F]/20 focus:border-[#D4A72C]"
                />
              </div>

              <Button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={isSubmitting}
                className="w-full md:w-auto bg-[#1E3A5F] hover:bg-[#1E3A5F]/90 text-white btn-lift"
              >
                {isSubmitting ? t('contact.sending') : t('contact.send')}
                <Send className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </div>

          {/* Contact Info Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-[#1E3A5F] rounded-sm p-8 sticky top-28 text-white">
              <h3 className="font-serif text-xl mb-6">{t('contact.contactInfo')}</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#D4A72C]" />
                  </div>
                  <div>
                    <p className="font-medium mb-1">{t('contact.address')}</p>
                    <a 
                      href="https://maps.app.goo.gl/zpX7mdR27SFsptg38"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-white/60 hover:text-[#D4A72C] transition-colors"
                    >
                      {t('contact.addressStreet')}<br />
                      {t('contact.addressCity')}<br />
                      {t('contact.addressCountry')}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#D4A72C]" />
                  </div>
                  <div>
                    <p className="font-medium mb-1">{t('contact.phone')}</p>
                    <a 
                      href="tel:+306957020999"
                      data-testid="contact-phone-link-1"
                      className="text-sm text-white/60 hover:text-[#D4A72C] transition-colors block"
                    >
                      +30 695 702 0999
                    </a>
                    <a 
                      href="tel:+306937334121"
                      data-testid="contact-phone-link-2"
                      className="text-sm text-white/60 hover:text-[#D4A72C] transition-colors block"
                    >
                      +30 693 733 4121
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#D4A72C]" />
                  </div>
                  <div>
                    <p className="font-medium mb-1">Email</p>
                    <a 
                      href="mailto:villa.aristotle.cfu@gmail.com"
                      data-testid="contact-email-link"
                      className="text-sm text-white/60 hover:text-[#D4A72C] transition-colors"
                    >
                      villa.aristotle.cfu@gmail.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/10">
                <p className="text-sm text-white/60">
                  <strong className="text-white">{t('contact.responseTimeLabel')}:</strong><br />
                  {t('contact.responseTimeText')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Mobile Booking */}
      <BookingWidget variant="sticky" />
    </div>
  );
};

export default Contact;
